import { Box, Flex, Select, Text, TextInput } from '@mantine/core'
import { createFormContext, zodResolver } from '@mantine/form'
import { usePathname } from 'next/navigation'
import { type HTMLAttributes, useCallback, useEffect } from 'react'

import { useAnalytics } from '@flecto/analytics'
import {
  useMutationCreateItemCategory,
  useMutationUpdateItemCategory,
  useQueryGetAllItemCategories,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import {
  type CoreItemCategory,
  type CoreItemCategoryPayload,
} from '@flecto/types'
import {
  type ItemPhotoForManager,
  PhotoManager,
  RichTextEditor,
  useNotifications,
} from '@flecto/ui'

import { useCategoryUpdateFormSchema } from './useCategoryUpdateFormSchema'

export const [
  CategoryUpdateFormProvider,
  useCategoryUpdateFormContext,
  useFormCategoryUpdate,
] = createFormContext<CoreItemCategoryPayload>()

type CategoryUpdateFormProps = HTMLAttributes<HTMLFormElement> & {
  category?: Partial<CoreItemCategory>
  onSuccess?: (newCategory: CoreItemCategory) => void
  onError?: () => void
  onValidationChange?: (isValid: boolean) => void
  onLoading?: (loading: boolean) => void
}

export function CategoryUpdateForm({
  category,
  onSuccess,
  onError,
  onValidationChange,
  onLoading,
  ...props
}: CategoryUpdateFormProps) {
  const { t } = useTranslation('saas', {
    keyPrefix: 'profile_inventory',
  })
  const { showNotification } = useNotifications()
  const { mutateAsync: createItemCategory } = useMutationCreateItemCategory()
  const { mutateAsync: updateItemCategory } = useMutationUpdateItemCategory()
  const { schema, initialValues } = useCategoryUpdateFormSchema({ category })
  const form = useFormCategoryUpdate({
    initialValues,
    validate: zodResolver(schema),
  })
  const { data: categories } = useQueryGetAllItemCategories()
  const track = useAnalytics('segment')
  const pathname = usePathname()

  const shouldDisableParentCategorySelect = Boolean(
    !categories?.item_category?.length ||
      (category?.subcategories?.length ?? 0) > 0 ||
      (category?.internal_id && (categories?.item_category?.length ?? 0) < 2)
  )

  const parentCategorySelectData = categories?.item_category.map(
    (category) => ({
      label: category.name,
      value: `${category.id}`,
    })
  )

  const photoObject = {
    links: {
      photo_url: category?.thumbnail_url || '',
    },
    main: true,
    type: 'category_photo',
  }

  const handleChangeDescription = useCallback(
    (text?: string) => {
      form.setFieldValue('description', text)
    },
    [form]
  )

  async function saveItemCategory() {
    try {
      const categoryData: Partial<CoreItemCategoryPayload> = {
        name: form.values.name,
        description: form.values.description || undefined,
        parent_id: form.values.parent_id || undefined,
        thumbnail_image_id: form.values.thumbnail_image_id || null,
      }

      let savedCategory: CoreItemCategory
      if (category?.internal_id) {
        onLoading?.(true)
        savedCategory = await updateItemCategory({
          categoryId: Number(category.internal_id),
          category: categoryData,
        })
        showNotification({
          type: 'success',
          message: t('notification_update_category_success'),
        })
      } else {
        savedCategory = await createItemCategory({ category: categoryData })
        showNotification({
          type: 'success',
          message: t('notification_create_category_success'),
        })
        track('CATEGORY__NEW', {
          source: pathname?.includes('inventory') ? 'settings' : 'product_form',
        })
      }

      onSuccess?.(savedCategory)
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_create_category_error'),
      })
      onError?.()
    } finally {
      onLoading?.(false)
    }
  }

  useEffect(() => {
    const isValid = Boolean(form.values.name)
    onValidationChange?.(isValid)
  }, [form.values.name, onValidationChange])

  return (
    <CategoryUpdateFormProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => saveItemCategory())}>
        <Flex direction='column' gap='xl'>
          <Flex direction='column' gap='sm'>
            <Box style={{ position: 'relative' }}>
              <TextInput
                error={form.getInputProps('name').error}
                aria-label='name'
                label={t('name')}
                placeholder={t('name_placeholder')}
                withAsterisk
                {...form.getInputProps('name')}
              />
            </Box>
          </Flex>

          <Flex direction='column'>
            <Text fw={500}>{t('parent')}</Text>
            <Text c='gray.7'>{t('parent_description')}</Text>
            <Select
              data={parentCategorySelectData}
              mt='sm'
              onChange={(value) => {
                form.setFieldValue('parent_id', Number(value))
              }}
              placeholder={t('parent_placeholder')}
              searchable
              size='md'
              defaultValue={`${category?.parent_id}` || undefined}
              disabled={shouldDisableParentCategorySelect}
            />
          </Flex>

          <Flex direction='column' gap='sm'>
            <Flex align='center' gap='xs'>
              <Text fw='500'>{t('description')}</Text>
            </Flex>

            <RichTextEditor
              error={form.getInputProps('description').error}
              onChange={handleChangeDescription}
              placeholder={t('description_placeholder')}
              updateIfChanged
              value={form.values.description}
              withToolbar={false}
            />
          </Flex>

          <Flex direction='column' w='100%'>
            <Flex align='center' gap='xs' mb='sm'>
              <Text fw='500'>{t('image')}</Text>
              <Text c='red.6' size='sm'>
                {form.getInputProps('thumbnail_image_id').error}
              </Text>
            </Flex>

            <PhotoManager
              hasError={Boolean(form.getInputProps('thumbnail_image_id').error)}
              onChange={(photoAttrs) => {
                form.setFieldValue(
                  'thumbnail_image_id',
                  photoAttrs.photo_image_id
                )
              }}
              photoObject={photoObject as ItemPhotoForManager}
              title={form.values.name || category?.name}
            />
          </Flex>
        </Flex>
      </form>
    </CategoryUpdateFormProvider>
  )
}
